import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentJointVerifyPhoneOtpSubmitInput,
  type EnrollmentJointVerifyPhoneOtpSubmitOutput,
  type EnrollmentJointVerifyPhoneData,
  type EnrollmentJointVerifyPhoneLinkSubmitInput,
  type EnrollmentJointVerifyPhoneLinkSubmitOutput,
  type EnrollmentJointVerifyPhoneDataInput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Joint VerifyPhone data
 * @param enrollmentVerifyPhoneDataInput - input data for retrieving verify phone information (optional)
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointVerifyPhoneData>
 * @example const response = await getEnrollmentJointVerifyPhoneData()
 */
export const getEnrollmentJointVerifyPhoneData = async (
  enrollmentVerifyPhoneDataInput?: EnrollmentJointVerifyPhoneDataInput,
  context?: DefaultContext,
): Promise<EnrollmentJointVerifyPhoneData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointVerifyPhoneData',
      query: `mutation getEnrollmentJointVerifyPhoneData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          isError
          errors
          activePerson {
            applicationId
            path
            progress
            email
            name {
              firstName
              lastName
            }
            phone {
              countryPhoneCode
              phoneNumber
            }
          }
        }
      }`,
    },
    {
      input: enrollmentVerifyPhoneDataInput ?? {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointVerifyPhoneData }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointVerifyPhoneData = createQueryHook<
  EnrollmentJointVerifyPhoneDataInput,
  EnrollmentJointVerifyPhoneData
>(getEnrollmentJointVerifyPhoneData)

/**
 * Submit Enrollment Joint VerifyPhone Link data
 * @param enrollmentVerifyPhoneSubmitInput - input data for verifying phone link
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointVerifyPhoneLinkSubmitOutput>
 * @example const response = await submitEnrollmentJointVerifyPhoneLinkData();
 */
export const submitEnrollmentJointVerifyPhoneLinkData = async (
  enrollmentVerifyPhoneSubmitInput: EnrollmentJointVerifyPhoneLinkSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointVerifyPhoneLinkSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointVerifyPhoneLinkData',
      query: `mutation submitEnrollmentJointVerifyPhoneLinkData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          isError
          errors
          activePerson {
            path
          }
        }
      }`,
    },
    {
      input: enrollmentVerifyPhoneSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointVerifyPhoneLinkSubmitOutput }
  return response.submitJointEnrollment
}

/**
 * Submit Enrollment Joint VerifyPhone OTP data
 * @param enrollmentVerifyPhoneSubmitInput - input data for verifying phone OTP
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointVerifyPhoneOtpSubmitOutput>
 * @example const response = await submitEnrollmentJointVerifyPhoneOtpData();
 */
export const submitEnrollmentJointVerifyPhoneOtpData = async (
  enrollmentVerifyPhoneSubmitInput: EnrollmentJointVerifyPhoneOtpSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointVerifyPhoneOtpSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointVerifyPhoneOtpData',
      query: `mutation submitEnrollmentJointVerifyPhoneOtpData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          isError
          errors
          activePerson {
            path
          }
        }
      }`,
    },
    {
      input: enrollmentVerifyPhoneSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointVerifyPhoneOtpSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointVerifyPhoneLinkData = createMutationHook<
  EnrollmentJointVerifyPhoneLinkSubmitInput,
  EnrollmentJointVerifyPhoneLinkSubmitOutput
>(submitEnrollmentJointVerifyPhoneLinkData)

export const useSubmitEnrollmentJointVerifyPhoneOTPData = createMutationHook<
  EnrollmentJointVerifyPhoneOtpSubmitInput,
  EnrollmentJointVerifyPhoneOtpSubmitOutput
>(submitEnrollmentJointVerifyPhoneOtpData)
